import { MapContainer, TileLayer } from "react-leaflet";
import React, { useEffect, useState } from "react";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import {
  DraggableMarker,
  MapEvents,
  RecenterMap,
} from "../components/MapUtils";
import { getAddress, searchLocation } from "../Data/LocationData";

const MapLocationModal = ({
  open,
  onClose,
  onConfirm,
  currentLat,
  currentLng,
  currentAddress,
}) => {
  const [position, setPosition] = useState({
    lat: currentLat ?? 0,
    lng: currentLng ?? 0,
  });

  const [search, setSearch] = useState("");
  const [address, setAddress] = useState(currentAddress || "");
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!open) return;

    if (currentLat != null && currentLng != null) {
      setPosition({ lat: currentLat, lng: currentLng });
      return;
    }

    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setPosition({
          lat: coords.latitude,
          lng: coords.longitude,
        });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 10000 },
    );
  }, [open, currentLat, currentLng]);

  useEffect(() => {
    const fetchAddress = async () => {
      setAddress("");
      const newAddress = await getAddress(position.lat, position.lng);
      setAddress(newAddress);
    };

    fetchAddress();
  }, [position]);

  const handleSuggestionClick = (item) => {
    const lat = Number(item.lat);
    const lng = Number(item.lon);

    setPosition({
      lat,
      lng,
    });

    setAddress(item.display_name);
    setSearch(item.display_name);
    setSuggestions([]);
  };

  const handleSearch = async () => {
    if (!search.trim()) return;

    try {
      const results = suggestions.length
        ? suggestions
        : await searchLocation(search);
      const firstResult = results?.[0];

      if (firstResult) handleSuggestionClick(firstResult);
    } catch (error) {
      console.error(error);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="flex bg-white w-[80vw] h-[80vh] rounded-lg overflow-hidden">
        <div className="w-[30%] relative">
          <div className="z-[9999] absolute top-6 left-6 bg-white shadow-lg rounded-md px-2 py-[7px] w-[124px] h-[30px] flex gap-x-2">
            <img src="/assets/icons/location.svg" alt="location" />
            <p className="font-aileron font-semibold text-xs leading-none text-dark-gray">
              Locate on map
            </p>
          </div>
          <div className="absolute top-[60px] left-6 z-[1000] w-[80%]">
            <input
              type="text"
              value={search}
              placeholder="Search location..."
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 focus:border-primary"
              onChange={async (e) => {
                const { value } = e.target;

                setSearch(value);

                if (value.trim().length < 3) {
                  setSuggestions([]);
                  return;
                }

                try {
                  const results = await searchLocation(value);
                  setSuggestions(results);
                } catch (error) {
                  console.error(error);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            {suggestions.length > 0 && (
              <div className="absolute top-[36px] left-0 z-[1000]  w-full bg-white rounded-md shadow-lg border border-light-grey max-h-[300px] overflow-y-auto">
                {suggestions.map((item) => (
                  <div
                    key={item.place_id}
                    className="cursor-pointer px-3 py-2 border-b border-light-grey last:border-b-0 hover:bg-background"
                    onClick={() => handleSuggestionClick(item)}
                  >
                    <p className="font-poppins text-xs text-black line-clamp-2">
                      {item.display_name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="relative h-full w-full">
          <MapContainer
            center={[position.lat, position.lng]}
            zoom={15}
            zoomControl={false}
            className="h-full w-full"
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            <MapEvents setPosition={setPosition} />

            <RecenterMap lat={position.lat} lng={position.lng} />

            <DraggableMarker
              position={[position.lat, position.lng]}
              setPosition={setPosition}
            />
          </MapContainer>

          <div
            className="z-[9999] absolute top-6 right-6 bg-primary w-8 h-8 rounded-md p-2"
            onClick={onClose}
          >
            <CloseIcon className="w-4 h-4 stroke-white cursor-pointer" />
          </div>
          <div className="z-[9999] absolute bottom-5 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-md  p-4 w-[363px] flex justify-between">
            <div>
              <p className="font-aileron font-normal text-xs leading-none text-dark-gray mt-1">
                {address || " "}
              </p>
              <p className="mt-3 font-poppins font-medium text-xs leading-none text-black">
                {position.lat.toFixed(6)},{position.lng.toFixed(6)}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={async () => {
                  onConfirm({
                    lat: position.lat,
                    lng: position.lng,
                    address,
                  });
                }}
                className="rounded-md px-3 py-2 bg-yellow text-black"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.66699 8.00033L6.21836 11.3337L13.3337 4.66699"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="rounded-md px-3 py-2 bg-background text-black"
                onClick={onClose}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapLocationModal;
